import type { ReactNode } from "react";
import type { IconType } from "react-icons";
import PageTemplate from "../../components/PageTemplate";
import SlidePageTransition from "../../components/transitions/SlidePageTransition";

export const userPageAnimationStyles = `
  @keyframes lds-page-enter {
    from {
      opacity: 0;
      transform: translateY(14px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes lds-section-enter {
    from {
      opacity: 0;
      transform: translateY(18px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .lds-page-enter {
    animation: lds-page-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .lds-section-enter {
    animation: lds-section-enter 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  @media (prefers-reduced-motion: reduce) {
    .lds-page-enter,
    .lds-section-enter {
      animation: none;
    }
  }
`;

type UserPageShellProps = {
  children: ReactNode;
  narrow?: boolean;
};

export const UserPageShell = ({ children, narrow = false }: UserPageShellProps) => (
  <PageTemplate title={null} wrapperClassName="max-w-none">
    <style>{userPageAnimationStyles}</style>
    <div
      className={`lds-page-enter mx-auto rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6 ${
        narrow ? "max-w-5xl" : "max-w-7xl"
      }`}
    >
      <SlidePageTransition className="space-y-5">{children}</SlidePageTransition>
    </div>
  </PageTemplate>
);

type UserHeroProps = {
  icon: IconType;
  eyebrow: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
};

export const UserHero = ({ icon: Icon, eyebrow, title, description, actions }: UserHeroProps) => (
  <section className="lds-section-enter relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
    <Icon
      aria-hidden="true"
      className="pointer-events-none absolute right-10 top-1/2 hidden -translate-y-1/2 text-[8rem] text-indigo-200/55 dark:text-indigo-400/10 lg:block"
    />
    <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-5">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
          <Icon aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
            {eyebrow}
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
            {title}
          </h1>
          {description ? (
            <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {actions ? <div className="relative z-10 flex shrink-0 flex-wrap items-center gap-3 lg:mr-36">{actions}</div> : null}
    </div>
  </section>
);
